/**
 * One node, at full reading depth: the claim, where it stands, every act the
 * app offers against it, and — folded — how the model got there.
 *
 * Extracted from the inspector's `NodePanel` so that a surface can render it
 * for a NAMED node rather than for "the selection" — the thread renders the
 * anchor's card at the head of its one scroll, and the anchor is not always
 * the selection. The component therefore takes the node as a prop and reads
 * everything else from the store itself, exactly as the panel did: widening
 * the prop surface to session/busy/ready would only create a second way for
 * those to arrive.
 *
 * THE CARD READS, THE COMPOSER ACTS (D347). A usability study with real model
 * calls found this card at 2,000px of content behind a 430px nested scroll: the
 * claim, then a wall of the model's reasoning, then six self-ratings nobody
 * could read, then the acts — so the acts came first for a while, and the
 * account went into folds. The acts have left the card since: every press it
 * carried is in the composer's modes (`ui/NodeActs.tsx`), pinned at the foot of
 * the column whatever this card's scroll. What is left is the reading, in the
 * reader's order: what the claim is, ONE line saying where it stands, the claim
 * at a glance — what has checked it from outside, beside the model's own
 * ratings, captioned for what they are — then why the model thinks it, open,
 * and the records of what happened when somebody looked. The forms the free
 * pair opens still open here, where what they write is read.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { lockHeld, producingOp, useStore } from '../core/store'
import { passFailureNote } from '../core/failure'
import {
  MOVE_REASON,
  OP_LABEL,
  OP_PAST,
  channelOf,
  claimRestatesLabel,
  kindChannelOf,
  kindLabelOf,
  modelsUsed,
  nextStepsOf,
  previousReasoningOf,
  reasoningOf,
  type ReasonNode,
  type Session,
} from '../core/types'
import { runMoveForNode, statusOf } from '../core/runFeed'
import { OBJECTION_STOOD_BELOW, objectionsStoodUnder } from '../core/endings'
import { nodeTextRetentionNotes, nodeTextRetentionStatus } from '../core/textRetention'
import { priorArtOf, priorArtParts } from '../core/priorWork'
import { lastSettledAudit } from '../core/auditReview'
import { Disclosure } from './components/Controls'
import { Fold } from './components/Fold'
import { Icon } from './components/Icon'
import { Chevron } from './components/Chevron'
import { VerbPrice } from './VerbPrice'
import { projectVerbUsd } from '../engine/run'
import { pressName, pressTail, useRouteReady } from './pressName'
import { ConjectureRecordBlock } from './ConjectureBlock'
import { FieldSweepBlock } from './SweepBlock'
import {
  AuditBlock,
  AuditRecord,
  OutcomeBlock,
  PanelBlock,
  SurveyPanel,
  VerificationPanel,
  scrollToCheckRecord,
  seatWhenFilled,
} from './Grounding'
import { ExperimentEvidenceBlock } from './ExperimentEvidenceBlock'
import { upNextRoom, upNextShut } from '../core/upNext'
import { focusCardTitle, takeRewriteOpener } from './cardFocus'
import { setComposerMode } from './composerMode'
import { pressRewrite } from './upNextPress'
import { useVoiceOn } from './useVoiceOn'
import { StandingBlock } from './Standing'
import { DiagnosticsBlock } from './Diagnostics'
import { useFigureBasis } from './useFigureBasis'
import { useGraphFrame } from '../canvas/useGraphFrame'
import { evidenceStaleOf } from '../core/rebase'
import {
  fmtRating,
  rewriteAuthorship,
  rewriteLoss,
  rewritePressLabel,
  selfRatingsOf,
} from '../core/rewrite'
import { COMPARISON_VERDICT_LABEL, STATUS_WORD, evidenceKinds } from '../core/vocabulary'
import { stateClauses, type StateClauseKey } from '../core/stateClauses'
import { secondLookChange } from '../core/statusWords'
import {
  refutedUnder,
  refutedUnderSentence,
  rewrittenOver,
  rewrittenOverSentence,
  type RefutedUnder,
} from '../core/taint'
import { claimReports, designTally } from '../core/claimEvidence'
import type { IndependenceTally } from '../core/genealogy'
import { oneStudyOf, oneStudySentence, type OneStudyReading } from '../core/cues'
import {
  DIMENSION_LABEL,
  VERDICT_GLOSS,
  comparisonStale,
  comparisonsFor,
  disagreePairOf,
  scopeReasons,
  topTests,
  unspecifiedCount,
} from '../core/disagreement'
import { COMPARE_TITLE } from '../core/nextStep'
import type { ContradictionAnalysis } from '../core/contradiction'
import { fmtUsd } from '../core/models'
import { groupReports, openFamiliesInLab } from './groupPress'
import { GapAnswer } from './GapAnswer'
import {
  disagreeEstimate,
  disagreeRefusal,
  openComparisonInLab,
  pressDisagree,
} from './disagreePress'

/**
 * Bring a node's revision editor onto the screen, after a press that opened it.
 *
 * `scrollToOutcomeForm`'s twin, here for the same reason it lives beside the
 * attribute it queries — and needed for a sharper one. `pokeRewrite` is a
 * broadcast with no scroll, and the editor opens where the claim is, at the
 * TOP of the card: the card's own Rewrite link sits below the verbs, and the
 * promote press is pressed from the conversation under the card, so either
 * way the editor can open out of sight. A poke that opens something nobody can
 * see is the same confusion as a poke that opens nothing.
 *
 * Targets the wrapper the editor opens INSIDE, never the editor: the editor
 * is rendered by an effect reacting to the poke, so on the poke's own frame
 * there is no `.editbox` to find. It waits a few frames for one to appear —
 * the wrapper centred before the editor grows it leaves the editor's foot,
 * and its priced button, below the fold — and centres the wrapper either way.
 */
export function scrollToRewriteEditor(nodeId: string): void {
  seatWhenFilled(
    () =>
      [...document.querySelectorAll<HTMLElement>('[data-edit-node]')].find(
        (el) => el.dataset.editNode === nodeId,
      ),
    (box) => box.querySelector('.editbox') !== null,
  )
}

/**
 * The node's own words: the label as the sheet's title, and the claim under it
 * only when it says something the title did not.
 *
 * The seed used to open as its whole prompt twice — at 22px, then the same
 * prompt again in body type directly beneath — because `startSession` writes
 * the typed words into both fields and this card printed both without asking.
 * The rule that decides is `claimRestatesLabel` in core, shared with the
 * thread's ancestor cards so the two cannot disagree about one node. Its own
 * component, and exported, so the rule can be rendered in a test without the
 * store behind the card. D90 has the reasoning, including why the paragraph
 * is removed rather than replaced.
 */
export function Statement({ node }: { node: ReasonNode }) {
  return (
    <>
      {/* Focusable, never a tab stop: where a door that opens the card puts
          focus (`focusCardTitle`). */}
      <h2 data-card-title={node.id} tabIndex={-1} className="insp-title">
        {node.label}
      </h2>
      {!claimRestatesLabel(node) && <p className="insp-claim">{node.claim}</p>}
    </>
  )
}

/** A status word as it sits inside the line: lower case, the table's own
 *  casing being the one a line OPENS with, which the line restores below. */
const low = (w: string): string => w.toLowerCase()

/** The clauses the report count goes in front of: it qualifies the reading, so
 *  it follows the reading group and precedes everything after it. */
const AFTER_READING: ReadonlySet<StateClauseKey> = new Set([
  'retracted',
  'outcome',
  'refuted',
  'rebase',
])

/**
 * Where the claim stands, in ONE line.
 *
 * Every annotation the card used to stack as its own banner — the ending, the
 * source reading, a withdrawn work it cites, a recorded result, the refuted
 * claim it stands on, the evidence rebase, an objection that stood below,
 * "rewritten by you" and the kind line's other suffixes — is one clause of this
 * sentence now, in that order, so the reader meets the claim's state in the
 * place they look for it and nowhere else. The long explanations
 * those banners carried did not go: the rebase's two paragraphs and the
 * retention notes are in "How this was made", and the full check record is
 * under the card's presses.
 *
 * "Not checked yet" is said out loud rather than left to an absence. A claim
 * with no reading and no recorded result has met nothing outside the model,
 * and a card that said nothing about that read as a card with nothing to say.
 * The seed's clause is the survey's (D143), never a reading: the question is
 * not a claim a check could settle.
 *
 * Exported and store-free, so its wording can be rendered in a test.
 */
export function StatusLine({
  node,
  stoodBelow,
  evidence,
  refuted,
  rewritten,
  reports,
  onReports,
  onRecord,
  onOpen,
}: {
  node: ReasonNode
  /** Objections that stood directly under this claim (`objectionsStoodUnder`). */
  stoodBelow: ReasonNode[]
  /** The refuted claim this one was reasoned from and not re-derived since
   *  (`refutedUnder`) — named, because "needs a second look" is the word for any
   *  changed record and a refuting result moves none. */
  refuted?: RefutedUnder | null
  /** What a claim the reader rewrote after it was refuted hangs under
   *  (`rewrittenOver`): not owed a re-derivation, and still under it. */
  rewritten?: RefutedUnder | null
  /** The newest evidence change this claim's reasoning stands before that the
   *  refutation above does not already name (`secondLookChange`), and the source
   *  record it came from. */
  evidence?: { rebasedAt?: number; source: Pick<ReasonNode, 'id' | 'label'> }
  /** This claim's reports and the independence units the Lab's genealogy counts
   *  them as (`claimTally`). Counted only past one report: a single source is one
   *  unit by definition, and the count would be of nothing — though what kind of
   *  evidence that one source is (`designTally`) is still said. */
  reports?: IndependenceTally
  /** Opens the Lab's Evidence tab, where the families behind the count are. */
  onReports?: () => void
  /** Brings the check record behind the reading onto the screen (D335). */
  onRecord?: () => void
  onOpen: (id: string) => void
}) {
  const isRoot = node.parentId === null
  const parts: Array<{ key: string; body: ReactNode; title?: string }> = []

  // How many of the reports that reading rests on are independent — the Lab's
  // count, beside the reading it qualifies — and what kind of evidence they are,
  // as the check labelled each source, in the same clause: "5 reports · 1
  // independent: 1 observational study, 4 news reports". A reading from five
  // reports of one study is one study's reading, and the line is where that is
  // read. The kinds are an annotation; nothing gates on them.
  const kinds = isRoot ? null : designTally(node)
  const words = kinds ? evidenceKinds(reports, kinds) : null
  const reportsPart = words
    ? {
        key: 'reports',
        title: [
          `${words}:`,
          reports && reports.reports > 1
            ? "copies of one artefact — the same DOI, the same text, near-verbatim text, or a shared trial, dataset or registry identifier — count once in the Lab's evidence genealogy, and a family you grouped there counts once too."
            : null,
          kinds?.size
            ? 'Each kind is what the source check that admitted the page read it as — a label, not a grading.'
            : null,
          'It counts; it does not change the reading.',
        ]
          .filter(Boolean)
          .join(' '),
        body: onReports ? (
          <button className="linkish" onClick={onReports}>
            {words}
          </button>
        ) : (
          words
        ),
      }
    : null

  // The clauses the strip and the path's cards print too, from one list
  // (`stateClauses`, D335) — in its order, with this line's titles and presses
  // laid over the words, and the report count after the reading it qualifies.
  for (const c of stateClauses(node, { refuted, evidenceChange: evidence })) {
    if (reportsPart && !parts.includes(reportsPart) && AFTER_READING.has(c.key))
      parts.push(reportsPart)
    switch (c.key) {
      case 'reading':
      case 'survey':
        // The reading is a press to the record behind it (D335): the stance
        // strip, the sources with their quotes and the provenance sit at the
        // card's foot, below the folds, and nothing connected this sentence to
        // them. The seed's survey the same, once there is one to go to. Plain
        // words where there is no record on the card.
        if (c.key === 'survey' && !node.survey) {
          parts.push({ key: c.key, body: c.word })
          break
        }
        parts.push({
          key: c.key,
          title: onRecord
            ? `The ${c.key === 'survey' ? 'survey' : 'check record'} behind this, further down`
            : undefined,
          body: onRecord ? (
            <button className="linkish" onClick={onRecord}>
              {/* Inside a press the line's own capital is not restored, so a
                  clause that opens the line restores it here. */}
              {parts.length === 0 ? c.word.charAt(0).toUpperCase() + c.word.slice(1) : c.word}
            </button>
          ) : (
            c.word
          ),
        })
        break
      // A conjecture's check is its prior-art search (D281, D288): the card says
      // the search's reading, or that none has read it, as the tree does (D289)
      // — never "not checked yet", which asks for a source check no conjecture
      // gets. `stateClauses` holds that exclusion for every surface.
      case 'prior-art':
        parts.push({
          key: c.key,
          body: c.word,
          title: 'An insight’s search is its prior-art search; no source check runs on one.',
        })
        break
      case 'unchecked':
        parts.push({
          key: c.key,
          body: c.word,
          title: 'No source check has read anything about this claim, and no result is recorded.',
        })
        break
      // A withdrawn work under the reading, beside the reading it qualifies: the
      // coach's cue and the passage's own title said so while this line read
      // "sources appear to agree" and stopped.
      case 'retracted':
        parts.push({
          key: c.key,
          body: c.word,
          title:
            'Crossref’s retraction data reported a work this check cites as retracted, withdrawn or removed when the check was made — the passage is marked in the check below. That is the publisher withdrawing the record, not a verdict on this claim.',
        })
        break
      // What it stands on that has since been refuted, by name and one press
      // away — the refuted claim, never the parent, which was not refuted.
      case 'refuted':
        if (refuted)
          parts.push({
            key: c.key,
            title: `${refutedUnderSentence(refuted)} That does not make this claim wrong.`,
            body: (
              <>
                {c.word}:{' '}
                <button className="linkish" onClick={() => onOpen(refuted.source.id)}>
                  {refuted.source.label}
                </button>
              </>
            ),
          })
        break
      case 'rebase':
        if (evidence)
          parts.push({
            key: c.key,
            title: evidence.rebasedAt
              ? `This claim is the earlier version of a branch rethought after the check on “${evidence.source.label}” changed.`
              : `This claim stands on “${evidence.source.label}”, whose source record changed after this reasoning was authored. That does not make it wrong.`,
            // Named, as the refutation beside it is: with two claims one press
            // away on one line, "open the source record" no longer said which.
            body: (
              <>
                {c.word} (the check on{' '}
                <button className="linkish" onClick={() => onOpen(evidence.source.id)}>
                  {evidence.source.label}
                </button>{' '}
                changed)
              </>
            ),
          })
        break
      // The ending, the seed's survey (D143), and a result that settled nothing
      // — one that settled it is already the ending (`statusWords.ts`).
      default:
        parts.push({ key: c.key, body: c.word })
    }
  }
  if (reportsPart && !parts.includes(reportsPart)) parts.push(reportsPart)
  if (stoodBelow.length > 0) {
    parts.push({
      key: 'stood',
      body: (
        <>
          {OBJECTION_STOOD_BELOW}:{' '}
          {stoodBelow.map((o, i) => (
            <span key={o.id}>
              {i > 0 && ', '}
              <button className="linkish" onClick={() => onOpen(o.id)}>
                {o.label}
              </button>
            </span>
          ))}
        </>
      ),
    })
  }
  // Rewritten after something above it was refuted: the clause the refutation
  // had, in words that are true of a sentence typed after it — the refuted
  // claim still named, one press away — and the rewrite in the same breath.
  if (node.editedAt !== undefined && rewritten)
    parts.push({
      key: 'edited',
      title: rewrittenOverSentence(rewritten, node.editedAt),
      body: (
        <>
          {low(STATUS_WORD.rewrittenOverRefuted)}:{' '}
          <button className="linkish" onClick={() => onOpen(rewritten.source.id)}>
            {rewritten.source.label}
          </button>
        </>
      ),
    })
  else if (node.editedAt)
    parts.push({
      key: 'edited',
      body: low(STATUS_WORD.revisedByYou),
      title: rewriteAuthorship(node) ?? undefined,
    })
  if (node.bridgeFrom) parts.push({ key: 'bridge', body: low(STATUS_WORD.bridge) })
  if (node.compact && !node.elaboration)
    parts.push({ key: 'compact', body: low(STATUS_WORD.compactLeaf) })
  if (node.elaboration)
    parts.push({
      key: 'detailed',
      body: `${low(STATUS_WORD.detailedBy)} ${node.elaboration.model.name}`,
    })
  if (nodeTextRetentionStatus(node) === 'truncated')
    parts.push({
      key: 'shortened',
      body: low(STATUS_WORD.textShortened),
      title: 'Part of this node’s text was not retained — how much is under How this was made.',
    })

  return (
    <p className="ncard-state">
      <i aria-hidden style={{ background: `var(--sem-${channelOf(node)})` }} />
      <span>
        {parts.map((p, i) => (
          <span key={p.key} title={p.title}>
            {i > 0 && ' · '}
            {i === 0 && typeof p.body === 'string'
              ? p.body.charAt(0).toUpperCase() + p.body.slice(1)
              : p.body}
          </span>
        ))}
      </span>
    </p>
  )
}

/* ── folds ──────────────────────────────────────────────────────────────── */

// The fold moved to its own module so the card's sections can fold too without
// importing the card back (D335); re-exported here for the callers that knew it
// as the card's.
export { Fold }

export function NodeCard({
  node,
  onHide,
}: {
  node: ReasonNode
  /** Folds the card to the thread's one-line strip. The thread owns whether the
   *  card is showing — a new selection opens it again — so the card only asks. */
  onHide?: () => void
}) {
  const session = useStore((s) => s.session)
  const rev = useStore((s) => s.rev)
  // The rewrite's paid press, in the editor: under a pass in flight it waits on
  // the Up next line while the line has room (`ui/upNextPress.ts`).
  const locked = useStore(lockHeld)
  const room = useStore(upNextRoom)
  // The rewrite's re-derivation is a move, and waits on the line as one does.
  const rewriteShut = useStore((s) => upNextShut(s, 'revise'))
  const toggleCollapse = useStore((s) => s.toggleCollapse)
  const revealNode = useStore((s) => s.revealNode)
  const select = useStore((s) => s.select)
  const focusRun = useStore((s) => s.focusRun)
  // Which run THIS tab is running, or null — the argument `statusOf` needs and
  // the record cannot supply, since a live run and an abandoned one are the same
  // bytes. Read as an id so a move landing does not re-render every card.
  const liveRunId = useStore((s) => (s.run && s.run.phase !== 'ended' ? s.run.id : null))
  const { folded } = useGraphFrame()

  const [editing, setEditing] = useState(false)
  const [draftClaim, setDraftClaim] = useState('')
  const [draftLabel, setDraftLabel] = useState('')

  // The reasoning route's key, not the selection's: the rewrite's re-derivation
  // runs on that role (`routeReady`).
  const ready = useRouteReady('deepen')
  const instruments = useStore((s) => s.instruments)
  const sigRewrite = useStore((s) => s.sigRewrite)

  // A half-written revision belongs to the node it was opened on, so moving off
  // that node closes it rather than pointing it at another claim.
  //
  // DECLARED BEFORE THE SIGNAL BELOW, and that order is the whole of it: effects
  // in one component run in declaration order, and a press that selects a node
  // and pokes the editor in the same tick delivers both changes in one render.
  // The other way round, the reset ran last and shut an editor the reader had
  // just asked for — reachable from the promote press on a detached
  // conversation, which is pinned to a node that is not the selected one.
  // Whichever arrives alone still behaves as it did. `OutcomeBlock` has held
  // this order all along, for the same reason.
  useEffect(() => {
    setEditing(false)
  }, [node.id])

  // The E key, the Rewrite link and the promote press arrive as the same
  // one-shot signal. Reacting to the CHANGE (not the value) means a signal
  // fired before this panel mounted does not reopen an editor nobody asked for.
  const seenRewrite = useRef(sigRewrite)
  useEffect(() => {
    if (sigRewrite === seenRewrite.current) return
    seenRewrite.current = sigRewrite
    if (node.parentId !== null) setEditing(true)
  }, [sigRewrite, node])

  // Focus follows the editor. It opens up where the claim is, and the press that
  // opened it — the composer's Record mode, or E — is elsewhere: a screen reader
  // heard nothing open, and typed words went nowhere. Closing it — Cancel, or its
  // paid press — took the focused button away and dropped focus on the page body.
  // So the claim's box takes focus as the editor opens, and as it closes, if
  // nothing else has it, the press that opened it (`takeRewriteOpener`) — or the
  // claim's title, after E. Without scrolling: the card's own scrolls place the
  // editor, and a pass the press started is followed by the feed.
  const claimBox = useRef<HTMLTextAreaElement | null>(null)
  const wasEditing = useRef(editing)
  useEffect(() => {
    if (editing === wasEditing.current) return
    wasEditing.current = editing
    const lost = !document.activeElement || document.activeElement === document.body
    if (editing) claimBox.current?.focus({ preventScroll: true })
    else if (lost) {
      const opener = takeRewriteOpener()
      if (opener) opener.focus({ preventScroll: true })
      else focusCardTitle(node.id)
    }
  }, [editing, node.id])

  // What the rewrite's re-derivation adds, as its name says it — one press's
  // worth (`projectVerbUsd`, the arithmetic `VerbPrice` prints).
  const modelId = useStore((s) => s.modelId)
  const roleModels = useStore((s) => s.roleModels)
  const moveEst = projectVerbUsd({ modelId, roleModels }, 'move')
  // …with the voice's line while the voice is on (D295).
  useVoiceOn()
  useFigureBasis()
  // From the LEDGER: a truncated `ops` array cannot shorten it, and a stamp on a
  // node is only worth printing when there is more than one model to distinguish.
  const models = useMemo(() => modelsUsed(session), [session, rev])

  // Seeded when the editor opens, and on nothing else. It used to reseed on
  // every `rev` — and a pass in flight bumps `rev` with each claim it lands, so
  // once a rewrite could be written and pressed under the lock, every landing
  // put the claim's words back over the reader's half-written ones.
  useEffect(() => {
    if (editing) {
      setDraftClaim(node.claim)
      setDraftLabel(node.label)
    }
  }, [editing, node.id])
  // What the rewrite's press would take off the board, read only while the
  // editor is open — the same reading `reviseNode` counts its toast from, and
  // the count a press under the lock is held to when its turn comes.
  const loss = useMemo(
    () => (editing ? rewriteLoss(session, node.id) : null),
    [editing, session, node.id, rev],
  )

  // `kindChannelOf`, not `channelOf`: this eyebrow names the kind of reasoning
  // move, and the status line under the claim names the epistemic state.
  // Tinting it from `channelOf` would make a refuted mechanism's "Mechanism"
  // magenta and say the same thing twice in two places.
  const tint = `var(--sem-${kindChannelOf(node)})`
  const isRoot = node.parentId === null
  const childCount = session.order.filter((id) => session.nodes[id]?.parentId === node.id).length
  const fold = folded.get(node.id) ?? 0
  const reasoning = reasoningOf(node)
  // The argument written for the words the reader replaced — kept, folded under
  // its own name, and never the "why" of the words on the card now.
  const previous = previousReasoningOf(node)
  const nextSteps = nextStepsOf(node)
  const evidenceStale = evidenceStaleOf(session, node.id)
  // Memoised on the node map inside `taintMap`, so this is a lookup per render.
  const refuted = refutedUnder(session, node.id)
  // …and, where the reader's rewrite came after it, what it still hangs under.
  const rewritten = refuted ? null : rewrittenOver(session, node.id)
  // The parent's half of an ending: an objection that stood below this claim is
  // said HERE, where the claim it held against is being read, and said as
  // reasoning — never as refuted.
  const stoodBelow = useMemo(() => objectionsStoodUnder(session, node.id), [session, node.id, rev])
  // The refutation says the same record's change more exactly, so a change it
  // already names is not said again beside it (`secondLookChange`).
  const evidenceChange = secondLookChange(evidenceStale?.changes, refuted ?? rewritten)
  const evidenceSource = evidenceChange ? session.nodes[evidenceChange.sourceId] : undefined
  // The Lab's evidence genealogy, asked about this one claim: its reports and
  // their independence units, and whether some of them may be one study. Built
  // once per document (`sessionEvidence`), whichever surface asks first.
  const tally = useMemo(
    // Past one report only, and asked only then: `reportBound` counts without
    // building the genealogy, which a large evidence record makes slow. The same
    // guard the report's clause asks through (`claimReports`).
    () => claimReports(session, node.id),
    [session, node.id],
  )
  const oneStudy = useMemo(
    () => (isRoot ? null : oneStudyOf(session, node.id)),
    [session, node.id, isRoot],
  )
  const retention = nodeTextRetentionNotes(node)
  // The rungs beneath the reading, for the wayfinding line in the provenance
  // fold. `hasTrace` is read through `producingOp` — the exact condition
  // `Trace` renders on — so the line never promises a deliberation the
  // disclosure beside it will not show. When a rewrite has overtaken that
  // pass, the trace names the earlier version (as `Trace` itself does, with
  // "Reasoned before your rewrite"), so the clause must not claim the old pass
  // produced the user's new wording.
  const producing = producingOp(session, node.id)
  const hasTrace = Boolean(producing?.thinking)
  const traceStale =
    node.editedAt !== undefined &&
    producing !== undefined &&
    producing.finishedAt !== undefined &&
    producing.finishedAt < node.editedAt
  // This node's OWN producing run, found through the op's `RunMove` — the same
  // join `Trace` makes, and the reason `RunMove` carries an op id while `Op`
  // carries no run field. A manual pass has no move and so no run, and no node
  // can borrow another run's account, because the join is by op. The feed's
  // `statusOf` tells a live run from an abandoned one, so a node produced by
  // the run happening right now can send you to watch it.
  //
  // Both halves in one lookup, because the row and the run are wanted together:
  // the row is what the run did HERE, and the run is where a reader is sent. The
  // row is on the record whether or not the model returned a trace to go with it,
  // which is why it is printed on its own line rather than inside `Trace` — that
  // disclosure renders nothing at all without `op.thinking`.
  const fromRun = producing ? runMoveForNode(session.runs, producing, node.id) : null
  const runStatus = fromRun ? statusOf(fromRun.run, liveRunId) : null

  /**
   * What the press offers, which is not the same sentence in all three cases.
   *
   * A run still going has no account yet — it gets a foot only once it comes to
   * something — so offering "the whole run's account" mid-run offers a thing that
   * is not there. And a node the reader has since rewritten is no longer that
   * run's product, so "the run it is part of" is false about the claim above:
   * past tense is the only true form for it.
   */
  const runOffer = traceStale
    ? 'the run it came from'
    : runStatus === 'running'
      ? 'the run it is part of, as it happens'
      : 'the whole run’s account'

  const pivot = (id: string) => {
    revealNode(id)
    select(id)
  }

  // "What would move this" was written with that reasoning, about the same
  // replaced words, so on a rewritten node it moves into the same fold.
  const hasWhy =
    Boolean(reasoning) ||
    Boolean(node.terminal && node.terminalReason) ||
    (nextSteps.length > 0 && !previous)
  const hasMade =
    !isRoot ||
    retention.length > 0 ||
    Boolean(refuted || rewritten) ||
    Boolean(evidenceChange && evidenceSource) ||
    Boolean(node.compact && !node.elaboration)

  return (
    <div className="ncard">
      {/* The marker `scrollToRewriteEditor` looks for, and it rides the WRAPPER
          rather than the editor itself — which is the difference between the
          scroll working and not. The editor is opened by an effect reacting to
          `sigRewrite`, so at the frame the press schedules there is no `.editbox`
          in the document yet and a query for one finds nothing. `OutcomeBlock`
          has always had this right for the same reason: its `data-outcome-node`
          sits on a group that is always rendered, and the form opens inside it. */}
      <div className="ncard-head" data-edit-node={node.id}>
        {/* What KIND of move this is, in the node's own hue — the sheet's
            eyebrow. Everything else the old kind line carried as suffixes is a
            clause of the status line now, or provenance in the last fold. */}
        <div className="ncard-kindrow">
          <span className="insp-status" style={{ color: tint }}>
            {kindLabelOf(node)}
          </span>
          {onHide && (
            <button
              type="button"
              className="ncard-hide"
              // The strip that replaces it says "Open card" and is collapsed;
              // this is the same disclosure from the open side.
              aria-label="Hide this card"
              aria-expanded
              onClick={onHide}
              title="Fold the card to one line — the conversation keeps the column"
            >
              Hide
              <Icon name="chevron" size={9} style={{ transform: 'rotate(-90deg)' }} />
            </button>
          )}
        </div>

        {editing ? (
          <div className="editbox">
            <p className="editbox-note">
              Rewrite what this node means. Everything derived from it will be discarded and
              re-reasoned from your version.
            </p>
            <input
              className="input"
              value={draftLabel}
              onChange={(e) => setDraftLabel(e.target.value)}
              placeholder="Short label"
              aria-label="Node label"
            />
            <textarea
              ref={claimBox}
              className="textarea"
              rows={5}
              value={draftClaim}
              onChange={(e) => setDraftClaim(e.target.value)}
              placeholder="State the claim as you understand it."
              aria-label="Node claim"
            />
            <div className="editbox-acts">
              {/* THE PRICED PRESS of the rewrite. The Rewrite link that opens
                  this editor says writing is free and re-deriving is the paid
                  pass; this button keeps that promise with the same verb and
                  the same arithmetic as every other one-pass press, because
                  this pass drops a subtree and reasons it again — and it says
                  how much it drops, because the study watched a rewrite take a
                  reader's sharpest insight and half her connections with no
                  word on screen. It wraps rather than clipping its price.
                  Under the lock it waits on the Up next line with the draft and
                  this count, and greys out only where the line has no room. */}
              <button
                className="btn btn--primary btn--wrap"
                disabled={draftClaim.trim().length < 8}
                // Shut by a full line the way the verbs are: still focusable, and
                // the press says why (`pressRewrite` refuses, the editor stays).
                aria-disabled={rewriteShut || undefined}
                // Named as the verbs are: its words, then what one press adds,
                // called an estimate — the face prints only the before → after pair.
                aria-label={pressName(
                  rewritePressLabel(loss ?? rewriteLoss(session, node.id)),
                  pressTail({ ready, est: moveEst }),
                )}
                onClick={() => {
                  const shown = loss ?? rewriteLoss(session, node.id)
                  const taken = pressRewrite(
                    node.id,
                    { claim: draftClaim, label: draftLabel },
                    shown,
                  )
                  if (taken !== 'refused') setEditing(false)
                }}
              >
                <Icon name="spark" size={13} />
                <span>{rewritePressLabel(loss ?? rewriteLoss(session, node.id))}</span>
                <VerbPrice verb="move" instead={ready ? undefined : STATUS_WORD.needsKey} />
              </button>
              <button className="btn" onClick={() => setEditing(false)}>
                Cancel
              </button>
            </div>
            {locked && room && (
              <p className="gnote gnote--quiet">
                A pass is in flight, so this waits up next. If more lands under this claim first, it
                asks you again before replacing it.
              </p>
            )}
          </div>
        ) : (
          <>
            <Statement node={node} />
            <StatusLine
              node={node}
              stoodBelow={stoodBelow}
              evidence={
                evidenceChange && evidenceSource
                  ? { rebasedAt: evidenceChange.rebasedAt, source: evidenceSource }
                  : undefined
              }
              refuted={refuted}
              rewritten={rewritten}
              reports={tally}
              onReports={openFamiliesInLab}
              onRecord={
                node.verification || node.survey ? () => scrollToCheckRecord(node.id) : undefined
              }
              onOpen={pivot}
            />
            {oneStudy && (
              <OneStudyLine
                reading={oneStudy}
                onGroup={() => groupReports(node.id)}
                onFamilies={openFamiliesInLab}
              />
            )}
            {/* A conjecture's record is part of what the node SAYS — what it
                departs from, its leap, its prediction — so it sits with the claim
                rather than among the measurements below. */}
            {node.conjecture && <ConjectureRecordBlock session={session} node={node} />}
            <GapAnswer session={session} node={node} />
          </>
        )}
      </div>

      {/* At a glance, under the line that says where it stands: what has
          checked this claim from outside the model, beside the model's own
          ratings — the second captioned for what it is, a sort order and not a
          probability, so the two are never read as one kind of number (D347). */}
      {!isRoot && <StatGrid node={node} childCount={childCount} models={models.length} />}

      {/* What the RECORDS say about this claim, above the acts that would add
          more: where it stands over the typed edges, the same claim elsewhere in
          the library, and the date it promised. None of it calls a model, and
          each line prints the rule it fired on — `ui/Standing.tsx`. One line at
          rest, the rest in its fold, unless the label is one to see (D335). */}
      {!isRoot && <StandingBlock node={node} />}

      <FieldSweepBlock session={session} node={node} />

      {/* Why this claim and the one it contradicts disagree: the Lab's
          structured comparison, pressed from the objection and read on both
          cards. One pass, priced on its button; after the verbs, because it is
          a pass about a pair rather than a move on the claim. */}
      {!isRoot && <DisagreeSection session={session} node={node} onOpen={pivot} />}

      {/* What a second opinion found — the panel's three readings, another
          house's cross-examination — where one has been asked. Its presses are
          the composer's Investigate. Behind the instruments switch, with a line
          where it would be rather than silence (below). */}
      {!isRoot && instruments && <SecondOpinion session={session} node={node} />}

      {/* The recorded result, and the form Record (R, or the composer's Record
          mode) opens — here, where what it writes will be read. */}
      {!isRoot && <OutcomeBlock session={session} node={node} />}

      {/* ── the model's own account, folded ─────────────────────────────── */}
      {hasWhy && (
        <Fold
          id="why"
          defaultOpen
          label={
            node.reasoningEditedAt !== undefined && !previous
              ? 'Why you think this'
              : 'Why the model thinks this'
          }
        >
          {reasoning && <p className="insp-reasoning">{reasoning}</p>}
          {node.terminal && node.terminalReason && (
            <p className="insp-reasoning" style={{ color: 'var(--ink-dead)', fontStyle: 'italic' }}>
              {node.terminalReason}
            </p>
          )}
          {/* What would move this claim is a fact ABOUT the claim, from the
              same pass, so it sits with the reasoning rather than among the
              buttons that spend money on it. */}
          {nextSteps.length > 0 && !previous && <NextSteps steps={nextSteps} />}
        </Fold>
      )}
      {/* A rewrite re-derives BELOW the node and never re-reasons the node itself,
          so the argument on it was written for the words the reader replaced.
          Kept, because it may be exactly what they rewrote against — but under
          its own name and closed by its own default, and `reasoningOf` keeps it
          out of every check, digest and pass from here on. */}
      {previous && (
        <Fold
          id="why-previous"
          label={
            previous.by === 'you'
              ? 'Your reasoning for the previous wording'
              : 'The model’s reasoning for the previous wording'
          }
        >
          <p className="gnote gnote--quiet">
            Written for the words you replaced, so it does not argue for the claim above. Kept here
            to read; no later pass is given it as this claim’s reasoning.
          </p>
          <p className="insp-reasoning">{previous.text}</p>
          {nextSteps.length > 0 && <NextSteps steps={nextSteps} />}
        </Fold>
      )}

      {node.evidence.length > 0 && (
        // The model's own pointers, recalled rather than looked up: nothing
        // here has met a source until the check says so (D124). The fold's
        // name says so before it is opened.
        <Fold
          id="pointers"
          label="Pointers to check (from the model’s memory)"
          meta={String(node.evidence.length)}
        >
          <ul className="bullets" style={{ '--bullet-tint': 'var(--sem-signal)' } as CSSProperties}>
            {node.evidence.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </Fold>
      )}

      {hasMade && (
        <Fold id="made" label="How this was made">
          {/* WHOSE WORDS, first, where they are the reader's: the claim above is
              theirs, and the stamp still names the model that drafted what they
              replaced (invariant 3) — both halves, in the one phrasing every
              surface uses (`rewriteAuthorship`). */}
          {node.editedAt !== undefined && (
            <p className="insp-deeper">
              <span className="eyebrow">Whose words</span>
              <span>{rewriteAuthorship(node)}</span>
            </p>
          )}
          {/* THE RUN'S OWN ROW FOR THIS NODE — the same row the feed shows and
              the same three facts in the same order, so the account and the
              node cannot describe one move two ways. WHOSE WORDS ARE ABOVE
              decides its label: `reviseNode` patches the target in place, so
              `producingOp` still resolves the ORIGINAL run op for a rewritten
              node, and unqualified this read as "a run wrote this" over a claim
              the reader had written themselves. */}
          {!isRoot && fromRun?.move && (
            <p className="insp-deeper">
              <span className="eyebrow">{traceStale ? 'Before your rewrite' : 'From a run'}</span>
              <span>
                Move {fromRun.move.at} · {OP_LABEL[fromRun.move.kind]} —{' '}
                {MOVE_REASON[fromRun.move.why]}
              </span>
            </p>
          )}
          {/* The reading ladder: the producing pass's full deliberation just
              below, and the whole run's account one tab away. Never on the
              seed, whose card is the user's own words with no pass behind it. */}
          {!isRoot && (hasTrace || fromRun) && (
            <p className="insp-deeper">
              <span className="eyebrow">Read deeper</span>
              {hasTrace && (
                <span>
                  {traceStale
                    ? 'the pass before your rewrite, just below'
                    : 'the pass that produced this, just below'}
                </span>
              )}
              {hasTrace && fromRun && <span aria-hidden> · </span>}
              {fromRun && <button onClick={() => focusRun(fromRun.run.id)}>{runOffer}</button>}
            </p>
          )}
          <Trace session={session} node={node} />

          {refuted && (
            <div className="rebase-notice" role="note">
              <span className="eyebrow">{STATUS_WORD.standsOnRefuted}</span>
              <p>{refutedUnderSentence(refuted)}</p>
              <p className="gnote gnote--quiet">
                That does not make this claim wrong — a conclusion can outlive the argument that
                reached it. It marks what is owed a re-derivation.
              </p>
            </div>
          )}
          {rewritten && node.editedAt !== undefined && (
            <div className="rebase-notice" role="note">
              <span className="eyebrow">{STATUS_WORD.rewrittenOverRefuted}</span>
              <p>{rewrittenOverSentence(rewritten, node.editedAt)}</p>
            </div>
          )}
          {evidenceChange && evidenceSource && (
            <div className="rebase-notice" role="note">
              <span className="eyebrow">
                {evidenceChange.rebasedAt ? STATUS_WORD.priorEvidence : STATUS_WORD.needsSecondLook}
              </span>
              <p>
                {evidenceChange.rebasedAt
                  ? `This claim is the earlier version of a branch rethought after the check on “${evidenceSource.label}” changed. The rethought version was written beside it; nothing was deleted.`
                  : `This claim stands on “${evidenceSource.label}”, whose source record changed after this reasoning was authored.`}
              </p>
              <p className="gnote gnote--quiet">
                That does not make this claim wrong. It identifies which evidence version the
                reasoning belongs to.
              </p>
            </div>
          )}
          {retention.map((note) => (
            <p className="gnote" key={note}>
              {note}
            </p>
          ))}
          {node.compact && !node.elaboration && (
            <p className="gnote gnote--quiet">
              This leaf carries the model’s reasoning kernel. More detail is requested only if a
              later portfolio develops it; opening this panel makes no model call.
            </p>
          )}
        </Fold>
      )}
      {!isRoot && !instruments && (
        <p className="gnote gnote--quiet" style={{ marginTop: 'var(--s2)' }}>
          Panels, cross-examination and the compiled findings are put away in explorer mode.
          Settings → Appearance → Instruments brings them back.
        </p>
      )}
      {!isRoot && instruments && <DiagnosticsBlock node={node} />}

      {/* The records of what happened when somebody looked, below the folds:
          the reading is already the status line's first clause, and a source
          list is long enough that above the folds it would bury them. */}
      {!isRoot && <ExperimentEvidenceBlock session={session} node={node} />}
      {!isRoot ? (
        <VerificationPanel session={session} node={node} part="record" />
      ) : (
        <SurveyPanel node={node} part="record" />
      )}

      {/* Focus and Prune live in the pane's footer — this group keeps only the
          fold, which is view state rather than an act on the branch. */}
      {!isRoot && childCount > 0 && (
        <div className="group" style={{ gap: 'var(--s2)' }}>
          <span className="eyebrow">This branch</span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              className={node.collapsed ? 'btn btn--on' : 'btn btn--outline'}
              onClick={() => toggleCollapse(node.id)}
            >
              <Icon name="chevron" size={13} />
              {node.collapsed ? 'Unfold' : 'Fold away'}
              <span className="kbd" style={{ marginLeft: 4 }}>
                H
              </span>
            </button>
          </div>
          <p style={{ fontSize: 'var(--fs-micro)', color: 'var(--text-3)' }}>
            {`${childCount} branch${childCount === 1 ? '' : 'es'} derived from here.${
              node.collapsed && fold > 0 ? ` ${fold} moves folded away.` : ''
            }`}
          </p>
        </div>
      )}
    </div>
  )
}

/**
 * "These 5 reports may describe 1 study; group them?" — the coach's `onestudy`
 * question, asked under the count it would change.
 *
 * The same reading the cue fires on (`oneStudyOf`) and the same press that
 * answers it (`groupReports`), so the card and the coach cannot offer two
 * different groups or group one two ways. Free: it writes the Lab's own
 * same-family overrides and calls no model, and its toast carries the undo.
 * Exported and store-free, so its words can be rendered in a test.
 */
export function OneStudyLine({
  reading,
  onGroup,
  onFamilies,
}: {
  reading: OneStudyReading
  onGroup: () => void
  onFamilies: () => void
}) {
  return (
    <p className="ncard-kin">
      <span>{oneStudySentence(reading)}</span>
      <span className="ncard-kin-acts">
        <button
          type="button"
          className="linkish"
          onClick={onGroup}
          title="Records a same-family decision for each join in the Lab's merge ledger, with the reason. No model call; the toast after it can undo it, and a “Keep separate” there outranks it."
        >
          Group them
        </button>
        <span aria-hidden>·</span>
        <button type="button" className="linkish" onClick={onFamilies}>
          see the families in the Lab
        </button>
      </span>
    </p>
  )
}

/**
 * Why a claim and the one it contradicts disagree — the block each stored
 * comparison gets, on both claims' cards, and the press that makes one, on the
 * objection's.
 *
 * Nothing here is computed: the verdict, the scope rows and the ranked tests
 * are the Lab's stored comparison (`core/disagreement.ts` reads it), and the
 * full record — every dimension, the moderators, the predictions and the cost
 * editor — is one press away in the Tests tab.
 */
function DisagreeSection({
  session,
  node,
  onOpen,
}: {
  session: Session
  node: ReasonNode
  onOpen: (id: string) => void
}) {
  const comparisons = useMemo(() => comparisonsFor(session, node.id), [session, node.id])
  if (!comparisons.length) return null
  return (
    <div className="group ncard-why-group">
      {comparisons.map((analysis) => (
        <DisagreeBlock
          key={analysis.id}
          analysis={analysis}
          nodeId={node.id}
          otherLabel={
            session.nodes[
              analysis.left.nodeId === node.id ? analysis.right.nodeId : analysis.left.nodeId
            ]?.label ?? ''
          }
          stale={comparisonStale(session, analysis)}
          onOpen={onOpen}
          onLab={() => openComparisonInLab(analysis)}
        />
      ))}
    </div>
  )
}

/**
 * The comparison's press for this objection and the claim it disagrees with,
 * where it has one — in the composer's Investigate (D347). The comparisons it
 * wrote stay on the card (`DisagreeSection`).
 */
export function DisagreePressFor({ session, node }: { session: Session; node: ReasonNode }) {
  const comparisons = useMemo(() => comparisonsFor(session, node.id), [session, node.id])
  const pair = disagreePairOf(session, node)
  if (!pair) return null
  const again = comparisons.some((a) =>
    [a.left.nodeId, a.right.nodeId].every((id) => id === pair.leftId || id === pair.rightId),
  )
  return (
    <DisagreePress
      nodeId={node.id}
      claimLabel={session.nodes[pair.leftId]?.label ?? ''}
      again={again}
    />
  )
}

/**
 * The priced press. Greyed while any pass is in flight or a run is live, with
 * the lock's own sentence as its title: this press does not wait on Up next
 * (`ui/disagreePress.ts` says why), so a press it could not honour is refused
 * before it is made rather than taken and dropped. Greyed, not `disabled`: its
 * own pass takes the lock, and a `disabled` button under the key that pressed it
 * dropped focus on the page body. It stays in the tab order, and a press under
 * the lock says why in that sentence (`pressDisagree`).
 */
function DisagreePress({
  nodeId,
  claimLabel,
  again,
}: {
  nodeId: string
  claimLabel: string
  again: boolean
}) {
  const busy = useStore((s) => s.busy)
  const run = useStore((s) => s.run)
  const session = useStore((s) => s.session)
  const modelId = useStore((s) => s.modelId)
  const keys = useStore((s) => s.keys)
  const customBase = useStore((s) => s.customBase)
  const customModel = useStore((s) => s.customModel)
  const view = { busy, run, session, modelId, keys, customBase, customModel }
  const refusal = disagreeRefusal(view, nodeId)
  const locked = busy || (run !== null && run.phase !== 'ended')
  const keyless = !locked && refusal !== null
  const est = disagreeEstimate({ modelId })
  const price = keyless
    ? STATUS_WORD.needsKey.toLowerCase()
    : est !== null
      ? `est. ${fmtUsd(est)}`
      : null
  const label = again ? 'Compare them again' : COMPARE_TITLE
  return (
    <button
      className="action action--wide ncard-why-press"
      style={{ '--act-tint': 'var(--sem-tension)' } as CSSProperties}
      aria-disabled={locked || undefined}
      title={
        refusal ??
        'One pass on the model you selected. An estimate that leans high; the verdict comes from closed rules over what the model extracts, not from its prose.'
      }
      aria-label={`${label}: this objection and “${claimLabel}”${price ? ` · ${price}` : ''}`}
      onClick={() => void pressDisagree(nodeId)}
    >
      <b style={{ width: '100%' }}>
        <Icon name="challenge" size={13} />
        {label}
        {price && <span className="ncard-why-est">{price}</span>}
      </b>
      <span>
        Compares this objection with “{claimLabel}” scope by scope — who, what, measured how and
        over what time — then names the tests that could tell them apart. The Lab’s Tests tab keeps
        the full record.
      </span>
    </button>
  )
}

/**
 * One stored comparison, compact: the verdict and what it means, the scope rows
 * that explain it, the two cheapest tests that could separate the pair, and the
 * door to the full record. Exported and store-free, so its words can be
 * rendered in a test.
 */
export function DisagreeBlock({
  analysis,
  nodeId,
  otherLabel,
  stale,
  onOpen,
  onLab,
}: {
  analysis: ContradictionAnalysis
  nodeId: string
  otherLabel: string
  stale: boolean
  onOpen: (id: string) => void
  onLab: () => void
}) {
  const here = analysis.left.nodeId === nodeId ? 'left' : 'right'
  const there = here === 'left' ? 'right' : 'left'
  const otherId = analysis[there].nodeId
  const reasons = scopeReasons(analysis)
  const tests = topTests(analysis)
  const unknown = unspecifiedCount(analysis)
  return (
    <section
      className="ncard-why"
      aria-label={`Why these disagree: this claim and “${otherLabel}”`}
    >
      <div className="ncard-why-head">
        <span className="eyebrow">Why these disagree</span>
        <span className="ncard-why-with">
          with{' '}
          <button type="button" className="linkish" onClick={() => onOpen(otherId)}>
            {otherLabel}
          </button>
        </span>
      </div>
      <p className="ncard-why-verdict">
        <b>{COMPARISON_VERDICT_LABEL[analysis.verdict]}</b>
        {stale && <span> · {low(STATUS_WORD.comparisonStale)}</span>}
        <span className="ncard-why-gloss"> — {VERDICT_GLOSS[analysis.verdict]}</span>
      </p>
      {reasons.length > 0 ? (
        <dl className="ncard-why-rows">
          {/* Which side is which, once, for the rows under it: each reads this
              card's claim first. A screen reader hears it in each row instead. */}
          <div className="ncard-why-legend" aria-hidden>
            <dt />
            <dd>this claim ↔ “{otherLabel}”</dd>
          </div>
          {reasons.slice(0, 3).map((row) => (
            <div key={row.dimension}>
              <dt>{DIMENSION_LABEL[row.dimension]}</dt>
              <dd>
                <span className="sr-only">this claim: </span>
                {row[here] || 'unspecified'} <span aria-hidden>↔</span>
                <span className="sr-only">; “{otherLabel}”: </span> {row[there] || 'unspecified'}
              </dd>
            </div>
          ))}
        </dl>
      ) : unknown > 0 ? (
        <p className="gnote gnote--quiet">
          {unknown} of {analysis.differences.length} scope fields were left unspecified on one side
          or both, and an empty field is uncertainty, not a difference.
        </p>
      ) : null}
      {tests.length > 0 && (
        <div className="ncard-why-tests">
          <span className="eyebrow">What could tell them apart</span>
          <ol>
            {tests.map((test) => (
              <li key={test.id}>{test.title}</li>
            ))}
          </ol>
        </div>
      )}
      <p className="gnote gnote--quiet">
        A model proposed each claim’s scope; closed rules reached the verdict and ranked the tests
        by their recorded effort, time and money. Proposals, not established experiments.{' '}
        <button type="button" className="linkish" onClick={onLab}>
          Open the full comparison in the Lab
        </button>
      </p>
    </section>
  )
}

/**
 * The second opinions, behind one press: the panel (three lenses, three
 * passes) and the cross-examination (another company's model). Both are
 * several passes, so they wait behind a door rather than sitting beside the
 * one-pass verbs as equals — and the door opens by itself when there is a
 * result behind it, because a panel's votes or an audit's finding are records
 * a reader should not have to go looking for.
 *
 * Otherwise it is a fold like its neighbours (D335): it wears their chevron,
 * and a reader who keeps it open keeps it open on the next card too. The
 * result still opens it whatever was remembered — that half is D157's promise.
 * Exported so that promise can be rendered in a test.
 */
export function SecondOpinion({ session, node }: { session: Session; node: ReasonNode }) {
  // What a second opinion FOUND — the panel's readings, another house's
  // cross-examination. Its presses are the composer's Investigate (D347), so with
  // nothing found there is nothing here: a fold that opened onto nothing would be
  // a door to an empty room. A result opens it, as it always did; the reader's
  // close holds for that card.
  const busy = useStore((s) => s.busy)
  const has = Boolean(node.panel || node.audit || lastSettledAudit(session, node.id, busy))
  const [open, setOpen] = useState(true)
  useEffect(() => setOpen(true), [node.id])
  if (!has) return null
  return (
    <div className="ncard-second" data-open={open || undefined}>
      <div className="tierline">
        <button
          type="button"
          className="cfold-head ncard-second-head"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <span className="cfold-label">Second opinion</span>
          <Chevron open={open} />
          <span className="cfold-meta" title="Several passes each">
            Panel · Cross-examine
          </span>
        </button>
      </div>
      {open && (
        <>
          <PanelBlock session={session} node={node} part="record" />
          <AuditBlock node={node} />
          <AuditRecord node={node} />
        </>
      )}
    </div>
  )
}

/** "What would move this", as the pass that wrote the reasoning left it. */
function NextSteps({ steps }: { steps: string[] }) {
  return (
    <div className="group" style={{ gap: 'var(--s2)' }}>
      <span className="eyebrow">What would move this</span>
      <ul className="bullets" style={{ '--bullet-tint': 'var(--sem-insight)' } as CSSProperties}>
        {steps.map((e, i) => (
          <li key={i}>{e}</li>
        ))}
      </ul>
    </div>
  )
}

/**
 * The claim at a glance (D347): what has examined it, beside the model's own
 * ratings — six meters in two groups, each group named for whose numbers it
 * holds, under the line that says where the claim stands.
 *
 * WHOSE NUMBERS. "CONF 74 / NOVEL 92 / TENSION 20" meant nothing to anyone in the
 * study: they are the model's own numbers, useful for ordering one model's claims
 * against each other and for nothing else, and a meter at the head of a card reads
 * as a measurement unless it says otherwise. So the three sit in a group of their
 * own, named as the model's, with the caption that says what they are — and
 * beside them the three counts of what has examined the claim since: the sources
 * a check kept, the lenses a panel read it under, the other company's model that
 * cross-examined it. Those are the numbers a reader can act on, so they come
 * first. They print on the record's own scale, "0.75" (`fmtRating`, D263).
 *
 * An empty count is not blank, it is a way to the press that would fill it: the
 * Sources meter opens the composer's Investigate mode, whose first press is the
 * source check, and Providers opens it where the cross-examination is offered.
 * Opening a mode spends nothing; the press there is the paid one, priced on its
 * row. Lenses stays informational, per the sheet. The bars under the counts are
 * coverage against a small denominator (four sources, three lenses, one other
 * provider) — a shape, not a score. Exported so its words can be rendered in a
 * test without the store.
 */
export function StatGrid({
  node,
  childCount,
  models,
}: {
  node: ReasonNode
  childCount: number
  /** How many models reasoned in this graph; above one, whose scale this is. */
  models: number
}) {
  const instruments = useStore((s) => s.instruments)
  const v = node.verification
  const p = node.panel
  const a = node.audit
  const srcN = v ? v.sources.length + (v.corpus?.cites.length ?? 0) : null
  // Null on a claim the reader rewrote: the three numbers rated the words they
  // replaced, and the export has always dropped them. The card drops them by
  // the export's own helper, and says why where they stood. Null too on a
  // conjecture's "departs from", carried onto the graph: its numbers are
  // placeholders no model gave.
  const rated = selfRatingsOf(node)
  const investigate = () => setComposerMode('investigate')

  const examined: Meter[] = [
    {
      name: 'Sources',
      value: srcN === null ? '—' : String(srcN),
      pct: srcN === null ? 0 : Math.min(1, srcN / 4),
      tint: 'color-mix(in srgb, var(--sem-insight) 85%, transparent)',
      // Never a source check on a conjecture (D281, D288): the meter says where
      // its search is instead of offering the one that can only read "nothing
      // found" on a proposal nobody has made.
      ...(v
        ? {}
        : node.conjecture
          ? {
              title:
                'An insight is not checked against sources. Its search is the prior-art search, under Investigate.',
            }
          : {
              go: investigate,
              title:
                'Nothing outside the graph has been consulted about this claim. Opens Investigate, where Check against sources is the first press — a paid pass, priced on its row.',
            }),
    },
    {
      name: 'Lenses',
      value: p ? String(p.votes.length) : '—',
      pct: p ? Math.min(1, p.votes.length / 3) : 0,
      tint: 'color-mix(in srgb, var(--sem-insight) 85%, transparent)',
    },
    {
      name: 'Providers',
      value: a ? '1' : '—',
      pct: a ? 1 : 0,
      tint: 'color-mix(in srgb, var(--sem-insight) 85%, transparent)',
      ...(a || !instruments
        ? {}
        : {
            go: investigate,
            title:
              'No model from another company has examined this claim. Opens Investigate, where Cross-examine is offered — a paid pass on the other provider.',
          }),
    },
  ]
  const own: Meter[] = rated
    ? [
        {
          name: 'Confidence',
          value: fmtRating(rated.confidence),
          pct: rated.confidence,
          tint: 'var(--sem-signal)',
        },
        {
          name: 'Novelty',
          value: fmtRating(rated.novelty),
          pct: rated.novelty,
          tint: 'var(--sem-insight)',
          // The meter is the model's own number and stays so; a check's
          // re-rating is the line under the glance, never folded into this one.
          // A conjecture's is its prior-art search's, in its record (D288).
          title: node.conjecture
            ? 'The model’s own rating. What a prior-art search found is in the insight’s record above.'
            : 'The model’s own rating. What a source check found about prior work is under these meters.',
        },
        {
          name: 'Tension',
          value: fmtRating(rated.tension),
          pct: rated.tension,
          tint: 'var(--sem-tension)',
        },
      ]
    : []

  return (
    <div className="glance">
      <div className="glance-groups">
        <div className="glance-group">
          <span className="eyebrow">Examined by</span>
          {examined.map((m) => (
            <MeterRow key={m.name} meter={m} />
          ))}
        </div>
        <div className="glance-group">
          <span className="eyebrow">The model’s own ratings</span>
          {own.map((m) => (
            <MeterRow key={m.name} meter={m} />
          ))}
          {rated ? (
            <p className="gnote gnote--quiet ncard-ratings">
              A sort order, not a probability.
              {models > 1 && node.model
                ? ` These are ${node.model.name}'s. Another model reasoned elsewhere in this graph and rates on its own scale, so they sort within a model and not across them.`
                : ''}
            </p>
          ) : node.editedAt === undefined && node.carriedFrom !== undefined ? (
            <p className="gnote gnote--quiet ncard-ratings">
              No confidence, novelty or tension here: no model rated this node — it carries what an
              insight departs from.
            </p>
          ) : (
            <p className="gnote gnote--quiet ncard-ratings">
              No confidence, novelty or tension here: the model rated the wording you replaced, and
              a rewrite does not rate yours.
            </p>
          )}
        </div>
      </div>
      {/* D142's line, directly under the meters that hold the novelty rating —
          wherever novelty shows, what a check found about prior work shows
          beside it. */}
      <PriorArtNote node={node} />
      <p className="statmeta">
        {childCount} derived · depth {node.depth}
        {models > 1 ? ` · ${node.model ? node.model.name : 'model not recorded'}` : ''}
      </p>
    </div>
  )
}

interface Meter {
  name: string
  value: string
  pct: number
  tint: string
  /** Where an empty count leads: the composer's mode with the press that would
   *  fill it. Free — opening a mode spends nothing. */
  go?: () => void
  title?: string
}

/** One meter: its name, its bar, its number — a press where it leads somewhere. */
function MeterRow({ meter: m }: { meter: Meter }) {
  const body = (
    <>
      <span className="gmeter-name">{m.name}</span>
      <span className="gmeter-track">
        <i style={{ width: `${m.pct * 100}%`, background: m.tint }} />
      </span>
      <span className="gmeter-value num">{m.value}</span>
    </>
  )
  return m.go ? (
    <button type="button" className="gmeter" data-go title={m.title} onClick={m.go}>
      {body}
    </button>
  ) : (
    <span className="gmeter" title={m.title}>
      {body}
    </span>
  )
}

/**
 * What a live check found about prior work, beside the self-rated novelty cell.
 *
 * "NOVEL 70" went on sitting over a claim its own check had re-rated to 0.30
 * against a paper that had already done it, because the measurement lived three
 * disclosures down in the check panel. The cell stays the model's number; this
 * line carries the closest kept source and the checking model's re-rating, apart,
 * so the two are never read as one. Exported so the line can be rendered in a
 * test without the store behind the card.
 */
export function PriorArtNote({ node }: { node: ReasonNode }) {
  const line = priorArtOf(node)
  if (!line) return null
  const p = priorArtParts(line)
  return (
    <p className="gnote gnote--quiet" data-prior-art>
      {p.before}
      {p.title &&
        (p.url ? (
          <a href={p.url} target="_blank" rel="noreferrer noopener">
            {p.title}
          </a>
        ) : (
          p.title
        ))}
      {p.after}
    </p>
  )
}

/**
 * The model's reasoning on the pass that produced this node.
 *
 * Attributed to the *op*, not the node: one call deliberates and then emits up
 * to five moves, and the trace is the deliberation that chose all of them. It
 * cannot honestly be split between them, so the disclosure says so instead.
 */
function Trace({ session, node }: { session: Session; node: ReasonNode }) {
  const op = useMemo(() => producingOp(session, node.id), [session, node.id])
  // The run's row for this op is NOT read here: this component returns null
  // without `op.thinking`, so the one node most in need of saying where it came
  // from, the one whose pass returned no reasoning at all, would say nothing.
  // The row is its own line beside this disclosure, off the same join.
  if (!op?.thinking) return null

  const n = op.produced.length
  const secs = op.finishedAt ? Math.max(1, Math.round((op.finishedAt - op.startedAt) / 1000)) : null
  // A rewrite patches the node and then re-derives *below* it, so the node is
  // the op's target and never in its `produced` — it keeps whatever produced it
  // originally, from before the rewrite. Saying so is the point.
  const stale =
    node.editedAt !== undefined && op.finishedAt !== undefined && op.finishedAt < node.editedAt
  const angles = op.portfolio?.parts.length ?? 0
  const meta = [
    OP_PAST[op.kind],
    angles > 1 ? `${angles} angles, one pass` : null,
    n > 1 ? `${n} nodes` : null,
    secs ? `${secs}s` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <Disclosure
      label={stale ? 'Reasoned before your rewrite' : 'The pass that produced this'}
      meta={meta}
      hint="The model's reasoning on the pass that produced this node"
    >
      {angles > 1 && (
        <p data-lead>
          One model pass worked {angles} compatible angles on the same snapshot. They share one
          deliberation; they are not independent judgements.
        </p>
      )}
      {n > 1 && (
        <p data-lead>
          One pass produced this and {n - 1} other{n === 2 ? '' : 's'} — the reasoning below is for
          all {n}.
        </p>
      )}
      {op.error && <p data-lead>{passFailureNote(op, 0, Boolean(op.thinking))}</p>}
      {op.thinking.split('\n\n').map((para, i) => (
        <p key={i}>{para}</p>
      ))}
      {/* Deliberately no number: a restored trace was clipped harder on the way
          to disk than the in-memory cap, and naming the wrong one is worse than
          naming none. */}
      {op.thinkingClipped && <p data-lead>The tail of this trace was dropped.</p>}
    </Disclosure>
  )
}
